import React, { useState, useEffect } from 'react';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { apiFetch } from '@/hooks/useFetch';
import { toast } from 'sonner';
import {
  Activity, CheckCircle2, AlertTriangle, AlertCircle, RotateCw, Clock,
  Database, Bell, Zap, Facebook, LogOut, ShieldAlert,
} from 'lucide-react';

/**
 * /admin → "System health" tab.
 *
 * One-glance dashboard so the admin doesn't have to dig through
 * separate tabs to know if everything is humming. Backed by the
 * /api/admin/system-health aggregate endpoint — single request,
 * five sections rendered as separate cards.
 *
 * Status semantics:
 *   green  → fresh data, healthy
 *   amber  → stale-but-recoverable (e.g. cron hasn't fired today)
 *   red    → broken (e.g. Meta token already expired)
 */

const tone = (level) => {
  switch (level) {
    case 'green':
      return { Icon: CheckCircle2, color: 'text-emerald-500', bg: 'bg-emerald-500/10', border: 'border-emerald-500/30', label: 'Healthy' };
    case 'amber':
      return { Icon: AlertTriangle, color: 'text-amber-500', bg: 'bg-amber-500/10', border: 'border-amber-500/30', label: 'Heads up' };
    case 'red':
      return { Icon: AlertCircle, color: 'text-red-500', bg: 'bg-red-500/10', border: 'border-red-500/30', label: 'Needs attention' };
    default:
      return { Icon: Activity, color: 'text-muted-foreground', bg: 'bg-muted/30', border: 'border-border', label: 'Unknown' };
  }
};

const StatusBadge = ({ level }) => {
  const t = tone(level);
  return (
    <Badge className={`${t.bg} ${t.color} border ${t.border} gap-1`}>
      <t.Icon className="h-3 w-3" /> {t.label}
    </Badge>
  );
};

// Heuristic: convert age in seconds into a green/amber/red level.
const ageLevel = (seconds, freshSec, staleSec) => {
  if (seconds == null) return 'red';
  if (seconds <= freshSec) return 'green';
  if (seconds <= staleSec) return 'amber';
  return 'red';
};

const SystemHealthTab = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data: d, error } = await apiFetch('/api/admin/system-health');
      if (error) {
        toast.error(typeof error === 'string' ? error : 'Failed to load system health.');
        return;
      }
      setData(d);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  if (!data) {
    return (
      <div className="p-6 text-sm text-muted-foreground" data-testid="system-health-loading">
        {loading ? 'Loading system health…' : 'No data yet.'}
      </div>
    );
  }

  const { scheduler, gsc_drainer, meta_token, vapid, quotas } = data;

  // Quota → level (green <50%, amber <90%, red ≥90%)
  const quotaLevel = quotas.google_indexing_pct < 50
    ? 'green' : (quotas.google_indexing_pct < 90 ? 'amber' : 'red');

  // Meta token → level (green > 14d, amber > 0d, red expired)
  let metaLevel = 'green';
  if (!meta_token.configured) metaLevel = 'amber';
  else if ((meta_token.days_until_expiry ?? -1) < 0) metaLevel = 'red';
  else if ((meta_token.days_until_expiry ?? 99) < 14) metaLevel = 'amber';

  return (
    <div className="space-y-4" data-testid="system-health-tab">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h2 className="text-xl font-semibold flex items-center gap-2">
            <Activity className="h-5 w-5 text-[#00A896]" /> System health
          </h2>
          <p className="text-xs text-muted-foreground">
            Snapshot generated {new Date(data.generated_at).toLocaleString()}
          </p>
        </div>
        <Button
          size="sm"
          variant="outline"
          onClick={load}
          disabled={loading}
          data-testid="system-health-refresh-btn"
        >
          <RotateCw className={`h-3.5 w-3.5 mr-1 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      {/* Scheduler cards row */}
      <div className="grid sm:grid-cols-2 gap-3">
        <Card data-testid="health-card-gsc">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="text-sm flex items-center gap-2">
                <Zap className="h-4 w-4 text-[#00A896]" /> GSC last submission
              </CardTitle>
              <StatusBadge level={ageLevel(scheduler.gsc_indexing.age_seconds, 86400, 86400 * 3)} />
            </div>
            <CardDescription className="text-xs">{scheduler.gsc_indexing.label}</CardDescription>
          </CardHeader>
          <CardContent className="text-xs space-y-1">
            <div><span className="text-muted-foreground">Source:</span> {scheduler.gsc_indexing.source}</div>
            <div><span className="text-muted-foreground">URLs:</span> {scheduler.gsc_indexing.url_count}</div>
          </CardContent>
        </Card>

        <Card data-testid="health-card-backup">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="text-sm flex items-center gap-2">
                <Database className="h-4 w-4 text-[#8B5CF6]" /> Last daily backup
              </CardTitle>
              <StatusBadge level={ageLevel(scheduler.daily_backup.age_seconds, 90000, 172800)} />
            </div>
            <CardDescription className="text-xs">{scheduler.daily_backup.label}</CardDescription>
          </CardHeader>
          <CardContent className="text-xs space-y-1">
            {Object.entries(scheduler.daily_backup.totals || {}).slice(0, 4).map(([col, n]) => (
              <div key={col} className="flex justify-between">
                <span className="text-muted-foreground">{col}</span>
                <span>{n}</span>
              </div>
            ))}
          </CardContent>
        </Card>

        <Card data-testid="health-card-blog">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="text-sm flex items-center gap-2">
                <Clock className="h-4 w-4 text-blue-500" /> Last blog publish
              </CardTitle>
              <StatusBadge level={ageLevel(scheduler.blog_publish.age_seconds, 86400 * 7, 86400 * 30)} />
            </div>
            <CardDescription className="text-xs">{scheduler.blog_publish.label}</CardDescription>
          </CardHeader>
          <CardContent className="text-xs truncate">{scheduler.blog_publish.title || '—'}</CardContent>
        </Card>

        <Card data-testid="health-card-snapshot">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="text-sm flex items-center gap-2">
                <Activity className="h-4 w-4 text-pink-500" /> Daily snapshot
              </CardTitle>
              <StatusBadge level={scheduler.daily_snapshot.date ? 'green' : 'amber'} />
            </div>
            <CardDescription className="text-xs">{scheduler.daily_snapshot.date || 'no snapshot yet'}</CardDescription>
          </CardHeader>
          <CardContent className="text-xs space-y-1">
            <div><span className="text-muted-foreground">Active users:</span> {scheduler.daily_snapshot.active}</div>
            <div><span className="text-muted-foreground">Total users:</span> {scheduler.daily_snapshot.total_users}</div>
          </CardContent>
        </Card>
      </div>

      {/* GSC drainer progress */}
      <Card data-testid="health-card-drainer">
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <CardTitle className="text-sm flex items-center gap-2">
              <Zap className="h-4 w-4 text-[#00A896]" /> GSC sitemap drainer
            </CardTitle>
            <StatusBadge level={gsc_drainer.enabled ? 'green' : 'amber'} />
          </div>
          <CardDescription className="text-xs">
            {gsc_drainer.enabled ? 'Auto-pinging sitemap URLs daily' : 'Drainer is OFF — turn it on in the GSC Indexing tab.'}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-2 text-xs">
          <Progress value={gsc_drainer.progress_pct} className="h-2" />
          <div className="flex flex-wrap justify-between gap-2 text-muted-foreground">
            <span>{gsc_drainer.pinged_recently_30d} of {gsc_drainer.sitemap_total} pinged in the last 30 days ({gsc_drainer.progress_pct}%)</span>
            {gsc_drainer.eta_days != null && (
              <span>ETA: ~{gsc_drainer.eta_days} day{gsc_drainer.eta_days === 1 ? '' : 's'} to drain remaining {gsc_drainer.remaining}</span>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Quota usage */}
      <Card data-testid="health-card-quota">
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <CardTitle className="text-sm">Google Indexing API daily quota</CardTitle>
            <StatusBadge level={quotaLevel} />
          </div>
        </CardHeader>
        <CardContent className="space-y-2 text-xs">
          <Progress value={Math.min(100, quotas.google_indexing_pct)} className="h-2" />
          <div className="text-muted-foreground">
            Used {quotas.google_indexing_used_today} of {quotas.google_indexing_daily_quota} Indexing API publish calls ({quotas.google_indexing_pct}%) today
          </div>
          {quotas.sitemap_drainer_pings_today > 0 && (
            <div className="text-[11px] text-muted-foreground/80">
              + {quotas.sitemap_drainer_pings_today} sitemap-drainer pings today (separate counter, shared 200/day Google quota in practice — keep an eye if total &gt; 200)
            </div>
          )}
        </CardContent>
      </Card>

      {/* Token + push */}
      <div className="grid sm:grid-cols-2 gap-3">
        <Card data-testid="health-card-meta">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="text-sm flex items-center gap-2">
                <Facebook className="h-4 w-4 text-blue-600" /> Meta page token
              </CardTitle>
              <StatusBadge level={metaLevel} />
            </div>
            <CardDescription className="text-xs">{meta_token.page_name || 'Not connected'}</CardDescription>
          </CardHeader>
          <CardContent className="text-xs">
            {meta_token.configured ? (
              <div>
                <span className="text-muted-foreground">Expires in:</span>{' '}
                <strong>{meta_token.days_until_expiry < 0 ? `${Math.abs(meta_token.days_until_expiry)} day(s) ago — REFRESH NOW` : `${meta_token.days_until_expiry} day(s)`}</strong>
              </div>
            ) : 'Connect Facebook in the Social Media tab to enable direct publishing.'}
          </CardContent>
        </Card>

        <Card data-testid="health-card-push">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="text-sm flex items-center gap-2">
                <Bell className="h-4 w-4 text-[#00E5CC]" /> Push notifications
              </CardTitle>
              <StatusBadge level={vapid.vapid_public_key_configured ? 'green' : 'amber'} />
            </div>
          </CardHeader>
          <CardContent className="text-xs space-y-1">
            <div><span className="text-muted-foreground">VAPID configured:</span> {vapid.vapid_public_key_configured ? 'Yes' : 'No'}</div>
            <div><span className="text-muted-foreground">Active subscriptions:</span> {vapid.subscriptions_total}</div>
          </CardContent>
        </Card>
      </div>

      {/* Force global logout — admin-only, breaks every existing JWT
          session in one click. Used after JWT_SECRET rotations, security
          incidents, or to flush the "stale token after deploy" issue. */}
      <ForceGlobalLogoutCard />
    </div>
  );
};

/** Force-global-logout card. Kept separate so it can be lazily rendered
    without bloating the main System Health request cycle. */
const ForceGlobalLogoutCard = () => {
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);
  const [confirming, setConfirming] = useState(false);

  useEffect(() => {
    (async () => {
      const { data } = await apiFetch('/api/admin/auth/global-logout-status');
      setStatus(data);
    })();
  }, []);

  const handleConfirm = async () => {
    setBusy(true);
    const { data, error } = await apiFetch(
      '/api/admin/auth/force-global-logout',
      { method: 'POST', body: {} },
    );
    if (error) {
      toast.error(error);
      setBusy(false);
      return;
    }
    // Replace our localStorage token with the fresh one the server
    // minted so we don't get kicked out by our own action.
    if (data?.fresh_admin_token) {
      localStorage.setItem('auth_token', data.fresh_admin_token);
    }
    toast.success(
      'Global logout complete — every existing session was invalidated.',
      { duration: 10000 },
    );
    setStatus({
      jwt_min_iat: data.jwt_min_iat,
      updated_at: data.human_time,
      ever_used: true,
    });
    setConfirming(false);
    setBusy(false);
  };

  const lastTime = status?.updated_at ? new Date(status.updated_at).toLocaleString() : null;

  return (
    <Card className="border-red-500/30 bg-red-500/5" data-testid="force-global-logout-card">
      <CardHeader className="pb-2">
        <CardTitle className="text-sm flex items-center gap-2">
          <ShieldAlert className="h-4 w-4 text-red-500" /> Force global logout
        </CardTitle>
        <CardDescription className="text-xs">
          Invalidates every existing JWT session in one click. Use after a JWT_SECRET rotation, security incident, or to clear the
          "401 / 403 after deploy" stale-token headache. Your own session is automatically refreshed so you stay signed in.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        {lastTime && (
          <p className="text-[11px] text-muted-foreground">
            <Clock className="inline h-3 w-3 mr-1" />
            Last triggered: <strong>{lastTime}</strong>
          </p>
        )}
        {!confirming ? (
          <Button
            variant="outline"
            size="sm"
            onClick={() => setConfirming(true)}
            className="border-red-500/40 hover:bg-red-500/10 text-red-700 dark:text-red-300"
            data-testid="force-logout-trigger-btn"
          >
            <LogOut className="h-3.5 w-3.5 mr-1" /> Force log everyone out
          </Button>
        ) : (
          <div className="space-y-2 rounded-md border-2 border-red-500/40 bg-red-500/10 p-3">
            <p className="text-xs font-medium">
              This will sign out every user (including all admins, all browsers, all devices). Continue?
            </p>
            <div className="flex gap-2">
              <Button
                variant="destructive"
                size="sm"
                disabled={busy}
                onClick={handleConfirm}
                data-testid="force-logout-confirm-btn"
              >
                {busy ? (
                  <>
                    <RotateCw className="h-3.5 w-3.5 mr-1 animate-spin" /> Logging everyone out…
                  </>
                ) : (
                  <>
                    <ShieldAlert className="h-3.5 w-3.5 mr-1" /> Yes, log everyone out
                  </>
                )}
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setConfirming(false)}
                disabled={busy}
              >
                Cancel
              </Button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default SystemHealthTab;
